import { useEffect, useState } from "react";
import Term from "../../term/Term";
import styles from "../css/LearningOptions.module.css";
import { LearningType } from "../model/LearningType";
import { useTranslation } from "react-i18next";
import GroupSelectScreen from "../../groups/GroupSelectScreen";
import Group from "../../groups/Group";
import {
  getLearningAnswerSpeechFromLocalStroage,
  getLearningIntervalUnitCountFromLocalStorage,
  getMemoPositionFromLocalStorage,
  getLearningPronunciationPositionFromLocalStroage,
  getQuestionTypeFromLocalStorage,
  getLearningSelectedGroupsFromLocalStorage,
  getLearningSelectedLevelFromLocalStroage,
  getLearningSortTypeFromLocalStorage,
  isOnLearningTextToSpeechFromLocalStorage,
  isOnShowingEnglishPronunciationSelectViewFromLocalStroage,
  setIsOnLearningTextToSpeechToLocalStorage,
  isOnWrongAnswerNoteFromLocalStorage,
  setIsOnWrongAnswerNoteToLocalStorage,
  setIsOnShowingEnglishPronunciationSelectViewToLocalStorage,
  setLearningAnswerSpeechToLocalStorage,
  setLearningIntervalUnitCountToLocalStroage,
  setMemoPositionToLocalStorage,
  setLearningPronunciationPositionToLocalStorage,
  setQuestionTypeToLocalStorage,
  setMultipleChoiceTimeLimitToLocalStorage,
} from "../../settings/util/learningSettings";
import { makeSelectedGroupsString } from "../../groups/groups";
import {
  isIncludeLevel,
  makeSelectedLevelsString,
  makeSortTypeString,
  toggleTermLevel,
} from "../../term/terms";
import BasicMenu from "../../common/BasicMenu";
import { SortType } from "../../settings/model/SortType";
import BigButton from "../../common/BigButton";
import OnevocaCheckBoxContainer from "../../common/OnevocaCheckBoxContainer";
import { TermLevel } from "../../term/custom_view/TermLevel";
import { QuestionType } from "../../settings/model/QuestionType";
import { PronunciationPosition } from "../../settings/model/PronunciationPosition";
import MediumButton from "../../common/MediumButton";
import CountPicker from "../../common/CountPicker";
import { CountPickerStyle } from "../../common/CountPickerStyle";
import {
  NO_LIMIT,
  getAnswerSpeechDisplayText,
  getMultipleChoiceTimeLimitDisplayText,
  getMultipleChoiceTimeLimitDisplayTextWithLimit,
} from "../util/learning";
import VoiceSettingScreen from "../../voice/view/VoiceSettingScreen";
import { toDecimalString } from "../../common/strings";
import ListSettingView from "../../common/ListSettingView";
import {
  fetchUserSettings,
  patchLearningAutoAdjustTermLevel,
} from "../../settings/data/UserSettingsRemoteDataSource";
import OnevocaDialog from "../../common/OnevocaDialog";
import LearningContinueResult from "../model/LearningContinueResult";
import { MemoPosition } from "../../settings/model/MemoPosition";
import { LearningTimeLimit } from "../model/LearningTimeLimit";
import { LearningAnswerSpeech } from "../model/LearningAnswerSpeech";
import { useBackToClose } from "../../../hooks/useBackToClose";
import { CloseRounded } from "@mui/icons-material";
import UIIconButton from "../../../components/ui/IconButton";
import LearningTypeSettingScreen from "./LearningTypeSettingScreen";

interface LearningOptionsProps {
  open: boolean;
  terms: Term[];
  type: LearningType | null;
  learningContinue: LearningContinueResult | null;
  mode: "beforeLearning" | "inLearning" | "beforeLearningDetail";
  onUpdateSelectedGroups: (groups: Group[]) => void;
  onUpdateSelectedLevels: (levels: TermLevel[]) => void;
  onUpdateSortType: (sortType: SortType) => void;
  onUpdateShowingPronunciationSelectView: () => void;
  onClickStartButton: (learningType: LearningType) => void;
  onClickContinueButton?: (
    learningType: LearningType,
    learningContinue: LearningContinueResult
  ) => void;
  onClickRestartButton?: () => void;
  onClose: () => void;
}

export default function LearningOptions(props: LearningOptionsProps) {
  useBackToClose(props.open, props.onClose);
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [openGroupSelect, setOpenGroupSelect] = useState(false);
  const [openSortSettingsMenu, setOpenSortSettingsMenu] = useState(false);
  const [openLevelSettingsMenu, setOpenLevelSettingsMenu] = useState(false);
  const [openQuestionTypeSettingsMenu, setOpenQuestionTypeSettingsMenu] =
    useState(false);
  const [openAnswerSpeechSettingsMenu, setOpenAnswerSpeechSettingsMenu] =
    useState(false);

  const [
    openPronunciationPositionSettingsMenu,
    setOpenPronunciationPositionSettingsMenu,
  ] = useState(false);
  const [openMemoPositionSettingMenu, setOpenMemoPositionSettingMenu] =
    useState(false);

  const [openIntervalUnitCountPicker, setOpenIntervalUnitCountPicker] =
    useState(false);
  const [openVoiceSetting, setOpenVoiceSetting] = useState(false);
  const [
    openMultipleChoiceTimeLimiSetting,
    setOpenMultipleChoiceTimeLimitSetting,
  ] = useState(false);
  const [openLearningDetailSettingView, setOpenLearningDetailSettingView] =
    useState(false);
  const [openStartOption, setOpenStartOption] = useState<boolean>(false);
  const [openNewStartAlertMessage, setOpenNewStartAlertMessage] =
    useState(false);

  const [selectedGroups, setSelectedGroups] = useState<string[]>();
  const [selectedGroupsString, setSelectedGroupsString] = useState("");
  const [selectedLevelsString, setSelectedLevelsString] = useState("");

  const [sortTypeString, setSortTypeString] = useState("");
  const [questionTypeString, setQuestionTypeString] = useState("");
  const [answerSpeechString, setAnswerSpeechString] = useState("");

  const [pronunciationPositionString, setPronunciationPositionString] =
    useState("");
  const [memoPositionString, setMemoPositionString] = useState("");

  const [intervalUnitCountString, setIntervalUnitCountString] = useState("");
  const [isOnTextToSpeech, setIsOnTextToSpeech] = useState(false);
  const [isOnWrongAnswerNote, setIsOnWrongAnswerNote] = useState(false);
  const [isOnAutoAdjustTermLevel, setIsOnAutoAdjustTermLevel] = useState(false);

  const [
    isOnShowingEnglishPronunciationSelectView,
    setIsOnShowingEnglishPronunciationSelectView,
  ] = useState(false);
  const [
    selectedMultipleChoiceTimeLimitString,
    setSelectedMultipleChoiceTimeLimitString,
  ] = useState("");

  useEffect(() => {
    if (props.open) {
      document.body.style.overflow = "hidden";
      updateSelectedGroupsString();
      updateSelectedLevelsString();
      updateSortTypeString();
      updateQuestionTypeString(getQuestionType());
      updatePronunciationPositionString();
      updateMemoPositionString();
      updateIntervalUnitCountString();
      updateIsOnTextToSpeech();
      updateIsOnWrongAnswerNote();
      updateIsOnAutoAdjustTermLevel();
      updateShowingEnglishPronunciationSelectView();
      updateMultipleChoiceTimeLimitString();
      updateAnswerSpeechString();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  function getTitle(): string | null {
    if (props.mode === "inLearning") {
      return t("Options");
    }

    switch (props.type) {
      case LearningType.flashcards:
        return t("Flashcard");
      case LearningType.multiplechoice:
        return t("MultipleChoiceTitle");
      case LearningType.spellingQuiz:
        return t("SpellingQuiz");
    }
    return null;
  }

  function getQuestionType(): QuestionType {
    return getQuestionTypeFromLocalStorage(
      props.type ?? LearningType.flashcards
    );
  }

  function handleOnClickContainer() {
    props.onClose();
  }

  function handleOnClickOptionsContainer(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
  }

  function handleOnClickSortButton(e: React.MouseEvent<HTMLDivElement>) {
    setAnchorEl(e.currentTarget);
    setOpenSortSettingsMenu(true);
  }

  function handleOnClickAnswerSpeechButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    setAnchorEl(e.currentTarget);
    setOpenAnswerSpeechSettingsMenu(true);
  }

  function handleOnClickQuestionTypeButton() {
    setOpenQuestionTypeSettingsMenu(true);
  }

  function handleOnClickMemoPositionButton() {
    setOpenMemoPositionSettingMenu(true);
  }

  function handleOnClickPronunciationButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    setAnchorEl(e.currentTarget);
    setOpenPronunciationPositionSettingsMenu(true);
  }

  function handleOnClickIntervalUnitCountButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    setAnchorEl(e.currentTarget);
    setOpenIntervalUnitCountPicker(true);
  }

  function handleOnClickVoiceSetting() {
    setOpenVoiceSetting(true);
  }

  function handleOnClickLevelButton(e: React.MouseEvent<HTMLDivElement>) {
    setAnchorEl(e.currentTarget);
    setOpenLevelSettingsMenu(true);
  }

  function handleOnClickMultipleChoiceTimeLimitButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    setAnchorEl(e.currentTarget);
    setOpenMultipleChoiceTimeLimitSetting(true);
  }

  function handleOnClickStartButton() {
    requestLearningStart();
  }

  function handleOnCLickRestartButton() {
    props.onClose();
    if (props.onClickRestartButton) {
      props.onClickRestartButton();
    }
  }

  function requestLearningStart() {
    if (props.terms.length === 0) {
      alert(t("LearningNoTermsMessage"));
      return;
    }

    if (props.learningContinue !== null) {
      setOpenStartOption(true);
    } else {
      props.onClickStartButton(props.type!);
    }
  }

  function updateSelectedGroupsString() {
    const string = makeSelectedGroupsString(
      getLearningSelectedGroupsFromLocalStorage()
    );
    setSelectedGroups(
      getLearningSelectedGroupsFromLocalStorage().map(
        (group: Group) => group.title
      )
    );
    setSelectedGroupsString(string);
  }

  function updateSelectedLevelsString() {
    const string = makeSelectedLevelsString(
      getLearningSelectedLevelFromLocalStroage(),
      "learning"
    );
    setSelectedLevelsString(string);
  }

  function updateMultipleChoiceTimeLimitString() {
    const string = getMultipleChoiceTimeLimitDisplayText();
    setSelectedMultipleChoiceTimeLimitString(string);
  }

  function updateShowingEnglishPronunciationSelectView() {
    const value = isOnShowingEnglishPronunciationSelectViewFromLocalStroage();
    setIsOnShowingEnglishPronunciationSelectView(value);
  }

  function updateSortTypeString() {
    const string = makeSortTypeString(getLearningSortTypeFromLocalStorage());
    setSortTypeString(string);
  }

  function getQuestionTypeText(questionType: QuestionType): string {
    switch (questionType) {
      case QuestionType.termFirst:
        return t("WordFirst");
      case QuestionType.definitionFirst:
        return t("MeanFirst");
      case QuestionType.pronFirst:
        return t("PronFirst");
      case QuestionType.random:
        return t("LearningTypeRandom");
      default:
        return "";
    }
  }

  function updateQuestionTypeString(questionType: QuestionType) {
    setQuestionTypeString(getQuestionTypeText(questionType));
  }

  function updateAnswerSpeechString() {
    setAnswerSpeechString(
      getAnswerSpeechDisplayText(getLearningAnswerSpeechFromLocalStroage())
    );
  }

  function getMemoPositionText(position: MemoPosition): string {
    switch (position) {
      case MemoPosition.onTerm:
        return t("MemoOnTerm");
      case MemoPosition.onDefinition:
        return t("MemoOnDefi");
      case MemoPosition.none:
        return t("MemoNone");
    }
  }

  function updateMemoPositionString() {
    setMemoPositionString(
      getMemoPositionText(
        getMemoPositionFromLocalStorage(props.type ?? LearningType.flashcards)
      )
    );
  }

  function updatePronunciationPositionString() {
    const position = getLearningPronunciationPositionFromLocalStroage();
    switch (position) {
      case PronunciationPosition.onTerm:
        setPronunciationPositionString(t("PronOnQuestion"));
        break;
      case PronunciationPosition.onDefinition:
        setPronunciationPositionString(t("PronOnAnswer"));
        break;
    }
  }

  function updateIntervalUnitCountString() {
    const countValue = getLearningIntervalUnitCountFromLocalStorage();
    const countDecimalString = toDecimalString(countValue);
    let string = "";
    if (Number(countValue) === NO_LIMIT) {
      string = t("LearningIntervalUnitCountAllType");
    } else {
      string = t("LearningIntervalUnitCountValueType", {
        value: countDecimalString,
      });
    }
    setIntervalUnitCountString(string);
  }

  function updateIsOnTextToSpeech() {
    setIsOnTextToSpeech(isOnLearningTextToSpeechFromLocalStorage());
  }

  function updateIsOnWrongAnswerNote() {
    setIsOnWrongAnswerNote(
      isOnWrongAnswerNoteFromLocalStorage(props.type ?? LearningType.flashcards)
    );
  }

  async function updateIsOnAutoAdjustTermLevel() {
    const userSettings = await fetchUserSettings();
    setIsOnAutoAdjustTermLevel(userSettings.learningAutoAdjustLevel);
  }

  function updateTermLevel(checked: boolean, level: TermLevel) {
    const newLevels = toggleTermLevel(
      checked,
      level,
      getLearningSelectedLevelFromLocalStroage()
    );
    props.onUpdateSelectedLevels(newLevels);
    updateSelectedLevelsString();
  }

  function updateIntervalUnitCount(count: number) {
    setLearningIntervalUnitCountToLocalStroage(count);
    updateIntervalUnitCountString();
  }

  if (props.open === false) {
    return null;
  }

  return (
    <>
      <div className={styles.stack}>
        <div
          className={styles.container}
          onClick={() => handleOnClickContainer()}
        >
          <div
            className={styles.optionsContainer}
            onClick={handleOnClickOptionsContainer}
          >
            <div className={styles.contentsContainer}>
              <div className={styles.titleRow}>
                <p className={styles.learningTitleText}>{getTitle()}</p>
                <UIIconButton
                  aria-label={t("CloseButton")}
                  icon={<CloseRounded />}
                  className={styles.closeButton}
                  onClick={() => props.onClose()}
                />
              </div>
              {props.mode === "beforeLearning" && (
                <div className={styles.settingContainer}>
                  <ListSettingView
                    title={t("GroupSelectToLearn")}
                    value={selectedGroupsString}
                    valueStyle="stringValue"
                    showingDivider={true}
                    handleOnClick={() => {
                      setOpenGroupSelect(true);
                    }}
                  />
                  <ListSettingView
                    title={t("QuestionLevel")}
                    value={selectedLevelsString}
                    valueStyle="stringValue"
                    handleOnClick={handleOnClickLevelButton}
                  />
                </div>
              )}
              {props.mode === "beforeLearning" && (
                <div className={styles.settingContainer}>
                  <ListSettingView
                    title={t("QuestionType")}
                    value={questionTypeString}
                    valueStyle="stringValue"
                    showingDivider={true}
                    handleOnClick={handleOnClickQuestionTypeButton}
                  />
                  <ListSettingView
                    title={t("LearningIntervalUnitCountSettingTitle")}
                    description={t("QuestionPerSectionCountSettingTitle")}
                    value={intervalUnitCountString}
                    valueStyle="stringValue"
                    handleOnClick={handleOnClickIntervalUnitCountButton}
                  />
                </div>
              )}
              {props.mode === "beforeLearningDetail" && (
                <div className={styles.settingContainer}>
                  <ListSettingView
                    title={t("LearningOrder")}
                    valueStyle="stringValue"
                    value={sortTypeString}
                    handleOnClick={handleOnClickSortButton}
                  />
                </div>
              )}
              {(props.mode === "inLearning" ||
                props.mode === "beforeLearningDetail") && (
                <div className={styles.settingContainer}>
                  <ListSettingView
                    title={t("LearningAutoSoundSettingTitle")}
                    valueStyle="booleanValue"
                    booleanValue={isOnTextToSpeech}
                    showingDivider={true}
                    handleOnBooleanValueChange={(isOn: boolean) => {
                      setIsOnLearningTextToSpeechToLocalStorage(isOn);
                      setIsOnTextToSpeech(isOn);
                    }}
                  />

                  {props.type !== LearningType.flashcards && (
                    <ListSettingView
                      title={t("LearningAnswerSpeechSettingTitle")}
                      valueStyle="stringValue"
                      value={answerSpeechString}
                      showingDivider={true}
                      handleOnClick={handleOnClickAnswerSpeechButton}
                    />
                  )}

                  <ListSettingView
                    title={t(
                      "ShowingEnglishPronunciationSelectViewSettingTitle"
                    )}
                    description={t(
                      "ShowingEnglishPronunciationSelectViewSettingDescription"
                    )}
                    valueStyle="booleanValue"
                    booleanValue={isOnShowingEnglishPronunciationSelectView}
                    showingDivider={true}
                    handleOnBooleanValueChange={(isOn: boolean) => {
                      setIsOnShowingEnglishPronunciationSelectView(isOn);
                      setIsOnShowingEnglishPronunciationSelectViewToLocalStorage(
                        isOn
                      );
                      props.onUpdateShowingPronunciationSelectView();
                    }}
                  />

                  <ListSettingView
                    title={t("voicesettings")}
                    valueStyle="stringValue"
                    handleOnClick={handleOnClickVoiceSetting}
                  />
                </div>
              )}
              {(props.mode === "inLearning" ||
                props.mode === "beforeLearningDetail") && (
                <div className={styles.settingContainer}>
                  <ListSettingView
                    title={t("PronPosition")}
                    valueStyle="stringValue"
                    value={pronunciationPositionString}
                    showingDivider={true}
                    handleOnClick={handleOnClickPronunciationButton}
                  />
                  <ListSettingView
                    title={t("MemoPositionTitle")}
                    valueStyle="stringValue"
                    value={memoPositionString}
                    handleOnClick={handleOnClickMemoPositionButton}
                  />
                </div>
              )}
              {(props.mode === "inLearning" ||
                props.mode === "beforeLearningDetail") &&
                (props.type === LearningType.multiplechoice ||
                  props.type === LearningType.spellingQuiz) && (
                  <div className={styles.settingContainer}>
                    <ListSettingView
                      title={t("WrongAnswerNoteSettingTitle")}
                      description={t("WrongAnswerNoteSettingDescription")}
                      valueStyle="booleanValue"
                      booleanValue={isOnWrongAnswerNote}
                      handleOnBooleanValueChange={(isOn: boolean) => {
                        setIsOnWrongAnswerNoteToLocalStorage(props.type!, isOn);
                        setIsOnWrongAnswerNote(isOn);
                      }}
                    />
                  </div>
                )}
              {props.mode === "beforeLearningDetail" && (
                <div className={styles.settingContainer}>
                  <ListSettingView
                    title={t("LearningAutoAdjustTermLevelTitle")}
                    description={t("LearningAutoAdjustTermLevelDescription")}
                    valueStyle="booleanValue"
                    booleanValue={isOnAutoAdjustTermLevel}
                    handleOnBooleanValueChange={(isOn: boolean) => {
                      patchLearningAutoAdjustTermLevel(isOn);
                      setIsOnAutoAdjustTermLevel(isOn);
                    }}
                  />
                </div>
              )}

              {props.mode === "beforeLearning" &&
                props.type === LearningType.multiplechoice && (
                  <div className={styles.settingContainer}>
                    <ListSettingView
                      title={t("MultipleChoiceTimeLimitSettingViewTitle")}
                      valueStyle="stringValue"
                      value={selectedMultipleChoiceTimeLimitString}
                      handleOnClick={handleOnClickMultipleChoiceTimeLimitButton}
                    />
                  </div>
                )}

              {props.mode === "beforeLearning" && (
                <div className={styles.settingContainer}>
                  <ListSettingView
                    title={t("LearningCustomSettingsTitle")}
                    value=""
                    valueStyle="stringValue"
                    handleOnClick={() => {
                      setOpenLearningDetailSettingView(true);
                      props.onUpdateShowingPronunciationSelectView();
                    }}
                  />
                </div>
              )}
              {(props.mode === "beforeLearning" ||
                props.mode === "beforeLearningDetail") && (
                <div className={styles.buttonsContainer}>
                  <BigButton
                    title={t("Start")}
                    variant="normal"
                    handleClick={() => handleOnClickStartButton()}
                  />
                </div>
              )}
              {props.mode === "inLearning" && (
                <div className={styles.inLearningButtonsContainer}>
                  <button
                    className={styles.restartButton}
                    onClick={() => handleOnCLickRestartButton()}
                  >
                    {t("LearningRestart")}
                  </button>
                  <div className={styles.inLearningButtonsRightContainer}>
                    <MediumButton
                      title={t("Continue")}
                      variant="primary"
                      onClick={() => props.onClose()}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <LearningOptions
          open={openLearningDetailSettingView}
          terms={props.terms}
          type={props.type}
          learningContinue={props.learningContinue}
          mode="beforeLearningDetail"
          onUpdateSelectedGroups={props.onUpdateSelectedGroups}
          onUpdateSelectedLevels={props.onUpdateSelectedLevels}
          onUpdateSortType={props.onUpdateSortType}
          onUpdateShowingPronunciationSelectView={() => {}}
          onClickStartButton={props.onClickStartButton}
          onClickContinueButton={props.onClickContinueButton}
          onClose={() => {
            setOpenLearningDetailSettingView(false);
          }}
        />

        <CountPicker
          open={openIntervalUnitCountPicker === true}
          defaultValue={getLearningIntervalUnitCountFromLocalStorage()}
          style={CountPickerStyle.learningIntervalUnitCount}
          onComplete={(count: number) => updateIntervalUnitCount(count)}
          onClose={() => {
            setOpenIntervalUnitCountPicker(false);
          }}
        />

        <BasicMenu
          isOpen={openSortSettingsMenu === true}
          anchorEl={anchorEl}
          buttons={[
            {
              title: t("Random"),
              handleOnClose: () => {
                setOpenSortSettingsMenu(false);
                props.onUpdateSortType(SortType.random);
                updateSortTypeString();
              },
            },
            {
              title: t("Latest"),
              handleOnClose: () => {
                setOpenSortSettingsMenu(false);
                props.onUpdateSortType(SortType.recent);
                updateSortTypeString();
              },
            },
            {
              title: t("Old"),
              handleOnClose: () => {
                setOpenSortSettingsMenu(false);
                props.onUpdateSortType(SortType.old);
                updateSortTypeString();
              },
            },
            {
              title: t("Alphabet"),
              handleOnClose: () => {
                setOpenSortSettingsMenu(false);
                props.onUpdateSortType(SortType.abc);
                updateSortTypeString();
              },
            },
          ]}
          handleOnClose={() => {
            setOpenSortSettingsMenu(false);
          }}
        />

        <BasicMenu
          isOpen={openAnswerSpeechSettingsMenu}
          anchorEl={anchorEl}
          buttons={[
            {
              title: t("LearningAnswerSpeechAll"),
              handleOnClose: () => {
                setOpenAnswerSpeechSettingsMenu(false);
                setLearningAnswerSpeechToLocalStorage(
                  LearningAnswerSpeech.always
                );
                updateAnswerSpeechString();
              },
            },
            {
              title: t("LearningAnswerSpeechIncorrect"),
              handleOnClose: () => {
                setOpenAnswerSpeechSettingsMenu(false);
                setLearningAnswerSpeechToLocalStorage(
                  LearningAnswerSpeech.onlyIncorrect
                );
                updateAnswerSpeechString();
              },
            },
            {
              title: t("LearningAnswerSpeechNone"),
              handleOnClose: () => {
                setOpenAnswerSpeechSettingsMenu(false);
                setLearningAnswerSpeechToLocalStorage(
                  LearningAnswerSpeech.none
                );
                updateAnswerSpeechString();
              },
            },
          ]}
          handleOnClose={() => {
            setOpenAnswerSpeechSettingsMenu(false);
          }}
        />

        <LearningTypeSettingScreen
          open={openQuestionTypeSettingsMenu}
          title={t("QuestionType")}
          currentLearningType={props.type}
          learningTypes={[
            LearningType.flashcards,
            LearningType.multiplechoice,
            LearningType.spellingQuiz,
          ]}
          getValueText={(learningType) =>
            getQuestionTypeText(getQuestionTypeFromLocalStorage(learningType))
          }
          getOptions={() =>
            [
              QuestionType.termFirst,
              QuestionType.definitionFirst,
              QuestionType.random,
              QuestionType.pronFirst,
            ].map((questionType) => ({
              label: getQuestionTypeText(questionType),
              isSelected: (type) =>
                getQuestionTypeFromLocalStorage(type) === questionType,
              onSelect: (type) =>
                setQuestionTypeToLocalStorage(type, questionType),
            }))
          }
          handleOnUpdateValue={() => updateQuestionTypeString(getQuestionType())}
          handleOnClose={() => {
            setOpenQuestionTypeSettingsMenu(false);
          }}
        />

        <LearningTypeSettingScreen
          open={openMemoPositionSettingMenu}
          title={t("MemoPositionTitle")}
          currentLearningType={props.type}
          learningTypes={[
            LearningType.flashcards,
            LearningType.multiplechoice,
            LearningType.spellingQuiz,
          ]}
          getValueText={(learningType) =>
            getMemoPositionText(getMemoPositionFromLocalStorage(learningType))
          }
          getOptions={() =>
            [
              MemoPosition.onTerm,
              MemoPosition.onDefinition,
              MemoPosition.none,
            ].map((position) => ({
              label: getMemoPositionText(position),
              isSelected: (type) =>
                getMemoPositionFromLocalStorage(type) === position,
              onSelect: (type) => setMemoPositionToLocalStorage(type, position),
            }))
          }
          handleOnUpdateValue={() => updateMemoPositionString()}
          handleOnClose={() => {
            setOpenMemoPositionSettingMenu(false);
          }}
        />

        <BasicMenu
          isOpen={openPronunciationPositionSettingsMenu === true}
          anchorEl={anchorEl}
          buttons={[
            {
              title: t("PronOnQuestion"),
              handleOnClose: () => {
                setOpenPronunciationPositionSettingsMenu(false);
                setLearningPronunciationPositionToLocalStorage(
                  PronunciationPosition.onTerm
                );
                updatePronunciationPositionString();
              },
            },
            {
              title: t("PronOnAnswer"),
              handleOnClose: () => {
                setOpenPronunciationPositionSettingsMenu(false);
                setLearningPronunciationPositionToLocalStorage(
                  PronunciationPosition.onDefinition
                );
                updatePronunciationPositionString();
              },
            },
          ]}
          handleOnClose={() => {
            setOpenPronunciationPositionSettingsMenu(false);
          }}
        />

        <BasicMenu
          isOpen={openMultipleChoiceTimeLimiSetting}
          anchorEl={anchorEl}
          buttons={[
            {
              title: getMultipleChoiceTimeLimitDisplayTextWithLimit(
                LearningTimeLimit.none
              ),
              handleOnClose: () => {
                setOpenMultipleChoiceTimeLimitSetting(false);
                setMultipleChoiceTimeLimitToLocalStorage(
                  LearningTimeLimit.none
                );
                updateMultipleChoiceTimeLimitString();
              },
            },
            {
              title: getMultipleChoiceTimeLimitDisplayTextWithLimit(
                LearningTimeLimit.slower
              ),
              handleOnClose: () => {
                setOpenMultipleChoiceTimeLimitSetting(false);
                setMultipleChoiceTimeLimitToLocalStorage(
                  LearningTimeLimit.slower
                );
                updateMultipleChoiceTimeLimitString();
              },
            },
            {
              title: getMultipleChoiceTimeLimitDisplayTextWithLimit(
                LearningTimeLimit.slow
              ),
              handleOnClose: () => {
                setOpenMultipleChoiceTimeLimitSetting(false);
                setMultipleChoiceTimeLimitToLocalStorage(
                  LearningTimeLimit.slow
                );
                updateMultipleChoiceTimeLimitString();
              },
            },
            {
              title: getMultipleChoiceTimeLimitDisplayTextWithLimit(
                LearningTimeLimit.normal
              ),
              handleOnClose: () => {
                setOpenMultipleChoiceTimeLimitSetting(false);
                setMultipleChoiceTimeLimitToLocalStorage(
                  LearningTimeLimit.normal
                );
                updateMultipleChoiceTimeLimitString();
              },
            },
            {
              title: getMultipleChoiceTimeLimitDisplayTextWithLimit(
                LearningTimeLimit.fast
              ),
              handleOnClose: () => {
                setOpenMultipleChoiceTimeLimitSetting(false);
                setMultipleChoiceTimeLimitToLocalStorage(
                  LearningTimeLimit.fast
                );
                updateMultipleChoiceTimeLimitString();
              },
            },
          ]}
          handleOnClose={() => {
            setOpenMultipleChoiceTimeLimitSetting(false);
          }}
        />

        <OnevocaCheckBoxContainer
          open={openLevelSettingsMenu === true}
          anchorEl={anchorEl}
          needCheckOneOrMore={true}
          items={[
            {
              title: t("Difficult"),
              checked: isIncludeLevel(
                TermLevel.difficult,
                getLearningSelectedLevelFromLocalStroage()
              ),
              onChangeValue(checked) {
                updateTermLevel(checked, TermLevel.difficult);
              },
            },
            {
              title: t("Familiar"),
              checked: isIncludeLevel(
                TermLevel.familiar,
                getLearningSelectedLevelFromLocalStroage()
              ),
              onChangeValue(checked) {
                updateTermLevel(checked, TermLevel.familiar);
              },
            },
            {
              title: t("Perfect"),
              checked: isIncludeLevel(
                TermLevel.perfect,
                getLearningSelectedLevelFromLocalStroage()
              ),
              onChangeValue(checked) {
                updateTermLevel(checked, TermLevel.perfect);
              },
            },
          ]}
          onClose={() => {
            setOpenLevelSettingsMenu(false);
          }}
        />

        <VoiceSettingScreen
          open={openVoiceSetting === true}
          handleOnUpdateValue={() => {}}
          handleOnClose={() => {
            setOpenVoiceSetting(false);
          }}
        />

        <GroupSelectScreen
          open={openGroupSelect === true}
          selectStyle="multiple"
          showAllGroup={true}
          showNoGroup={true}
          showGroupAddButton={false}
          selectedGroups={selectedGroups}
          handleOnSelectGroups={(groups: Group[]) => {
            props.onUpdateSelectedGroups(groups);
            updateSelectedGroupsString();
          }}
          handleOnClose={() => {
            setOpenGroupSelect(false);
          }}
        />

        <OnevocaDialog
          open={openNewStartAlertMessage}
          title={t("Notice")}
          message={t("LearningContinueNewStartAlertMessage")}
          buttons={[
            {
              title: t("Cancel"),
              style: "solid",
              onClick: () => {
                setOpenNewStartAlertMessage(false);
              },
            },
            {
              title: t("StartNewLearningButton"),
              style: "normal",
              onClick: () => {
                if (props.type === null) return;
                props.onClickStartButton(props.type);
              },
            },
          ]}
        />

        <OnevocaDialog
          open={openStartOption}
          title={t("LearningContinueMessage")}
          message={""}
          buttons={[
            {
              title: t("ContinueLearningButton"),
              style: "solid",
              onClick: () => {
                if (
                  props.type === null ||
                  props.learningContinue === null ||
                  props.onClickContinueButton === undefined
                )
                  return;
                props.onClickContinueButton(props.type, props.learningContinue);
              },
            },
            {
              title: t("StartNewLearningButton"),
              style: "normal",
              onClick: () => {
                setOpenNewStartAlertMessage(true);
              },
            },
            {
              title: t("Cancel"),
              style: "cancel",
              onClick: () => {
                setOpenStartOption(false);
              },
            },
          ]}
          onVerticalStyle={true}
        />
      </div>
    </>
  );
}
